import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";

interface GuardedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
  fallbackPath?: string;
  fallbackComponent?: React.ComponentType;
}

export function GuardedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallbackPath = "/unauthorized",
  fallbackComponent: FallbackComponent,
}: GuardedRouteProps) {
  const { has, hasAny, hasAll } = usePermissions();
  const location = useLocation();

  // Vérification simple des permissions sans mémorisation complexe
  let hasAccess = true;

  try {
    if (requiredPermission) {
      hasAccess = has(requiredPermission);
    } else if (requiredPermissions && requiredPermissions.length > 0) {
      hasAccess = requireAll ? hasAll(requiredPermissions) : hasAny(requiredPermissions);
    }
  } catch (error) {
    console.warn('Erreur lors de la vérification des permissions:', error);
    hasAccess = false; // En cas d'erreur, refuser l'accès par sécurité
  }

  if (!hasAccess) {
    if (FallbackComponent) {
      return <FallbackComponent />;
    }
    
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Convenience components for common patterns
export function RequirePermission({ 
  permission, 
  children, 
  fallback = null 
}: { 
  permission: string; 
  children: React.ReactNode; 
  fallback?: React.ReactNode;
}) {
  const { has } = usePermissions();
  
  try {
    if (!has(permission)) {
      return <>{fallback}</>;
    }
  } catch (error) {
    console.warn('Erreur dans RequirePermission:', error);
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

export function RequireAnyPermission({ 
  permissions, 
  children, 
  fallback = null 
}: { 
  permissions: string[]; 
  children: React.ReactNode; 
  fallback?: React.ReactNode;
}) {
  const { hasAny } = usePermissions();
  
  try {
    if (!hasAny(permissions)) {
      return <>{fallback}</>;
    }
  } catch (error) {
    console.warn('Erreur dans RequireAnyPermission:', error);
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}

export function RequireAllPermissions({ 
  permissions, 
  children, 
  fallback = null 
}: { 
  permissions: string[]; 
  children: React.ReactNode; 
  fallback?: React.ReactNode;
}) {
  const { hasAll } = usePermissions();
  
  try {
    if (!hasAll(permissions)) {
      return <>{fallback}</>;
    }
  } catch (error) {
    console.warn('Erreur dans RequireAllPermissions:', error);
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}